import React, { useState, useRef, useEffect } from 'react';
import { Grid as KGrid, GridColumn as Column, GridToolbar, GridNoRecords } from '@progress/kendo-react-grid';
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { orderBy, filterBy } from '@progress/kendo-data-query';
import '@progress/kendo-theme-material';
import { GridCell } from '@progress/kendo-react-grid';
import { formatDate } from '../utils';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { useIntl } from 'react-intl';
import { toZonedTime, toDate } from 'date-fns-tz';

const ADJUST_PADDING = 4;
const COLUMN_MIN = 4;



function GridContainer(props) {

    return (
        <div className="mdl-grid mdl-grid--no-spacing station-grid--container" style={{ justifyContent: 'center' }}>

            <div className="mdl-cell mdl-cell--12-col">
                <div className='mdl-grid'>
                    {props.children}
                </div>
            </div>

        </div>
    );
}

const DropdownFilterCell = (props) => {
    let hasValue = (value) => Boolean(value && value !== props.defaultItem);
    const onChange = (event) => {
        hasValue = hasValue(event.target.value);
        props.onChange({
            value: hasValue ? event.target.value : "",
            operator: hasValue ? "eq" : "",
            syntheticEvent: event.syntheticEvent,
        });
    };
    const onClearButtonClick = (event) => {
        event.preventDefault();
        props.onChange({
            value: "",
            operator: "",
            syntheticEvent: event,
        });
    };
    return (
        <div className="k-filtercell">
            <DropDownList
                data={props.data}
                onChange={onChange}
                value={props.value || props.defaultItem}
                defaultItem={props.defaultItem}
            />
            <Button
                title="Clear"
                disabled={!hasValue(props.value)}
                onClick={onClearButtonClick}
                icon="filter-clear"
            />
        </div>
    );
};

const ApiFilterCell = (props) => {
    const intl = useIntl();
    return <DropdownFilterCell
        {...props}
        data={[
            'Zentra',
            'Ech2o',
            'Fieldclimate',
            'Neatmon',
            'Agronic',
            'Talgil',
            'Qampo',
            'Davis',
            'Wiseconn',
            'E-Smart',
            'Galcon',
            'Ag-Zoom',
            'INIA',
            'Weenat',
        ]}
        defaultItem={intl.formatMessage({ id: "app.stationGrid.selectVendor", defaultMessage: "All" })}
    />
};



function DateCell(props) {
    const { dataItem } = props;
    const intl = useIntl();
    const isRTL = intl.locale.startsWith('ar');
    return (
        <td dir={isRTL ? 'rtl' : 'ltr'} >
            {dataItem.last_transmission ?
                intl.formatDate(
                    toZonedTime(toDate(dataItem.last_transmission), dataItem?.details?.timezone),
                    {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        timeZone: dataItem?.details?.timezone
                    }
                ) :
                ''}
        </td>
    )
}

function MapLinkCell(props) {
    const { dataItem } = props;
    const [disabled, setDisabled] = useState(false);
    const [link, setLink] = useState('')

    useEffect(() => {
        let lat, lng;
        if (dataItem.details) {
            if (dataItem.details.hasOwnProperty('location')) {
                lat = dataItem.details.location.lat;
                lng = dataItem.details.location.lng;
                setLink(`http://maps.google.com/maps?t=k&q=loc:${lat}+${lng}`);
            }
        } else {
            setDisabled(true)
        }
    }, [dataItem])

    return (
        <td>{
            disabled ?
                <i className='material-icons-outlined text-gray-500' style={{ cursor: 'not-allowed' }}>location_off</i> :
                <a href={link} target="_blank" >
                    <span className='hover:border-b-2 hover:border-b-blue-600'>
                        <i className='material-icons-outlined text-blue-600'>pin_drop</i>
                        <i className='material-icons-outlined text-blue-600' style={{ fontSize: '.9em', verticalAlign: 'top', marginLeft: -3 }}>north_east</i>
                    </span>

                </a>}
        </td>
    )
}

function UsersCell(props) {
    const { dataItem } = props;

    return <td>
        <div className='!h-[50px] overflow-scroll'>
            {dataItem.users.map(user => <span key={user.username}> {user.username} </span>)}
        </div>
    </td>
}
function InvalidSensorCell(props) {
    const { dataItem } = props;

    return <td>
        {dataItem.details?.sensors_without_data &&
            `${dataItem.details?.no_sensors_without_data} (${dataItem.details?.sensors_without_data}%)`}
    </td>
}

function BatteryCell(props) {
    const intl = useIntl();
    const { dataItem } = props;
    const battery = dataItem.details?.battery;

    return <td>
        {battery !== undefined && battery !== null ? intl.formatNumber(battery) : ''}
    </td>
}

function CommandCell({ edit, update, cancel, remove, editField }, intl) {
    return props => {
        // render() {
        const { dataItem } = props;
        const inEdit = dataItem[editField];

        return inEdit ? (
            <td className="k-command-cell !min-w-[200px] !w-full xl:!h-[71px] !h-[71px] flex justify-center">
                <button
                    className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md k-grid-save-command"
                    onClick={() => update(dataItem)}
                >
                    {intl.formatMessage({ id: "app.stationGrid.commandCdel.save", defaultMessage: "Save" })}
                </button>
                <button
                    className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md k-grid-cancel-command"
                    onClick={() => cancel(dataItem)}
                >
                    {intl.formatMessage({ id: "app.stationGrid.commandCdel.cancel", defaultMessage: "Cancel" })}
                </button>
            </td>
        ) : (
            <td className="k-command-cell !min-w-[200px] !w-full xl:!h-[71px] !h-[71px] flex justify-center">
                <button
                    className="k-primary k-button k-button-md k-button-solid k-button-solid-base k-rounded-md k-grid-edit-command"
                    onClick={() => edit(dataItem)}
                >
                    {intl.formatMessage({ id: "app.stationGrid.commandCdel.edit", defaultMessage: "Edit" })}
                </button>
                <button
                    className="k-button k-button-md k-button-solid k-button-solid-base k-rounded-md k-grid-delete-command"
                    style={{ color: 'red' }}
                    onClick={() => remove(dataItem)}
                >
                    {intl.formatMessage({ id: "app.stationGrid.commandCdel.delete", defaultMessage: "Delete" })}
                </button>
            </td>
        );
        // }
    }
};

function StationGrid(props) {
    const minGridWidth = React.useRef(0);
    const grid = React.useRef(null);
    const [applyMinWidth, setApplyMinWidth] = React.useState(false);
    const [gridCurrent, setGridCurrent] = React.useState(0);

    const [sort, setSort] = useState([{ field: 'serial', dir: 'asc' }]);
    const [filter, setFilter] = useState([]);
    const [skip, setSkip] = useState(0);
    const [pageSize] = useState(10);
    const _export = useRef(null);
    const intl = useIntl();

    const cellWidth = 200;

    const Commands = CommandCell({
        edit: props.edit,
        update: props.update,
        cancel: props.cancel,
        remove: props.remove,
        editField: "inEdit"
    }, intl);

    // Denormalize users array -> comma-separated usernames for filtering
    const preparedData = React.useMemo(() => {
        return (props.data || []).map(item => ({
            ...item,
            usersText: (item?.users || []).map(u => u.username).join(', ')
        }));
    }, [props.data]);

    const columns = [
        {
            field: 'api',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.vendor', defaultMessage: 'Vendor' }),
            minWidth: 180,
            filterCell: ApiFilterCell,
            editable: false
        },
        {
            field: 'sensors_without_data',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.sensors', defaultMessage: 'Inactive Sensors' }),
            minWidth: 140,
            filterable: false,
            cell: InvalidSensorCell
        },
        {
            field: 'name',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.name', defaultMessage: 'Name' }),
            dynamic: true, // mark for growing width
            minWidth: cellWidth,
        },
        {
            field: 'serial',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.serial', defaultMessage: 'Serial' }),
            dynamic: true,
            editable: false,
            minWidth: cellWidth,
        },
        {
            field: 'last_transmission',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.lastRecord', defaultMessage: 'Last Record' }),
            cell: DateCell,
            dynamic: true,
            editable: false,
            minWidth: cellWidth,
        },
        {
            field: 'details.battery',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.Battery', defaultMessage: 'Battery %' }),
            editable: false,
            minWidth: 100,
            cell: BatteryCell
        },
        {
            field: 'usersText', // use denormalized string for filtering
            title: intl.formatMessage({ id: 'app.admin.stationGrid.users', defaultMessage: 'Users' }),
            cell: UsersCell,    // keep the custom renderer
            dynamic: true,
            editable: false,
            minWidth: cellWidth,
        },
        {
            field: 'notes',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.notes', defaultMessage: 'Notes' }),
            dynamic: true,
            minWidth: cellWidth,
        },
        {
            field: 'details',
            title: intl.formatMessage({ id: 'app.admin.stationGrid.map', defaultMessage: 'View in Map' }),
            cell: MapLinkCell,
            editable: false,
            filterable: false,
            sortable: false,
            minWidth: 100,
        },
        {
            cell: Commands,
            editable: false,
            filterable: false,
            sortable: false,
            minWidth: 200,
        }
    ];


    React.useEffect(() => {
        grid.current = document.querySelector('.k-grid');
        window.addEventListener('resize', handleResize);
        columns.map(item => item.minWidth !== undefined ? minGridWidth.current += item.minWidth : minGridWidth.current);
        setGridCurrent(grid.current.offsetWidth);
        setApplyMinWidth(grid.current.offsetWidth < minGridWidth.current);
    }, []);

    const handleResize = () => {
        if (grid.current.offsetWidth < minGridWidth.current && !applyMinWidth) {
            setApplyMinWidth(true);
        } else if (grid.current.offsetWidth > minGridWidth.current) {
            setGridCurrent(grid.current.offsetWidth);
            setApplyMinWidth(false);
        }
    };
    const setWidth = minWidth => {
        if (minWidth === undefined) {
            minWidth = 0;
        }
        let width = applyMinWidth ? minWidth : minWidth + (gridCurrent - minGridWidth.current) / columns.length;
        if (width >= COLUMN_MIN) {
            width -= ADJUST_PADDING;
        }
        return width;
    };

    function pageChange(e) {
        setSkip(e.page.skip);
    }

    const downloadXls = () => {
        _export.current.save();
    }

    const filteredData = filterBy(orderBy(preparedData, sort), filter);
    return (
        <div ref={props.stationsRef} className='mdl-cell mdl-grid--no-spacing mdl-cell--12-col mdl-shadow--2dp station-grid'>
            <ExcelExport
                data={preparedData?.map(item => ({
                    ...item,
                    users: item.usersText // export readable usernames
                }))}
                ref={_export}
                fileName="Stations Grid"
            >
                <KGrid
                    data={filteredData.slice(skip, skip + pageSize)}
                    total={filteredData.length} // ✅ Correct total after filtering
                    scrollable={'virtual'}
                    className='!h-[600px]'
                    rowHeight={30}
                    sortable
                    sort={sort}
                    onSortChange={e => setSort(e.sort)}
                    filterable
                    skip={skip}
                    pageSize={pageSize}
                    onPageChange={pageChange}
                    filter={filter}
                    onFilterChange={e => { setFilter(e.filter); setSkip(0); }}
                    editField='inEdit'
                    onItemChange={props.onChange}
                >
                    <GridNoRecords>
                        {intl.formatMessage({ id: 'app.admin.graph_grid.no_data', defaultMessage: 'No Records Available' })}
                    </GridNoRecords>
                    <GridToolbar>
                        <div className='mdl-grid mdl-grid--no-spacing mdl-cell--12-col'>
                            <div className='mdl-cell mdl-cell--6-col ag-grid-title'>
                                {intl.formatMessage({ id: 'app.admin.stationGrid.station', defaultMessage: 'Stations' })}
                            </div>
                            <div className='ag-grid-link-button--ctn mdl-cell mdl-cell--6-col'>
                                <button className="mdl-button mdl-js-button mdl-button--primary" style={{ marginRight: 5 }} onClick={downloadXls}>{intl.formatMessage({ id: 'app.admin.stationGrid.download', defaultMessage: 'Download' })}</button>
                                <button className='ag-link-button ag-link-button--green-underline' onClick={(e) => props.setData(e, 'All')}>{intl.formatMessage({ id: 'app.admin.stationGrid.all', defaultMessage: 'All' })}</button>
                                <button className='ag-link-button ag-link-button--green-underline' onClick={(e) => props.setData(e, 'Normal')}>{intl.formatMessage({ id: 'app.admin.stationGrid.normal', defaultMessage: 'Normal' })}</button>
                                <button className='ag-link-button ag-link-button--green-underline' onClick={(e) => props.setData(e, 'Lagging')}>{intl.formatMessage({ id: 'app.admin.stationGrid.lagging', defaultMessage: 'Lagging' })}</button>
                                <button className='ag-link-button ag-link-button--green-underline' onClick={(e) => props.setData(e, 'Low Battery')}>{intl.formatMessage({ id: 'app.admin.stationGrid.lowBattery', defaultMessage: 'Low Battery' })}</button>
                            </div>
                            <div>
                                <span className='text-gray-500 text-md'>
                                    {intl.formatMessage(
                                        {
                                            id: 'app.admin.stationGrid.inactivateSensors',
                                            defaultMessage: "sensors are inactive"
                                        }, { percent: <span className='font-semibold text-amber-500'>{props.percentOfOrgsSensorsWithoutData}%</span> })}
                                </span>
                            </div>
                        </div>

                    </GridToolbar>
                    {columns.map((col, index) => (
                        <Column
                            key={index}
                            field={col.field}
                            title={col.title}
                            editable={col.editable}
                            filterable={col.filterable}
                            sortable={col.sortable}
                            cell={col.cell}
                            filterCell={col.filterCell}
                            width={setWidth(col.minWidth)}
                        />
                    ))}
                </KGrid>
            </ExcelExport>
        </div>
    );
}


export default StationGrid;
export { GridContainer };